import { SliderModal, VStack } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { ScrollView } from 'native-base';
import React from 'react';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../routes/types';
import { useFixedFeeActions } from '../../../store/fixedFee/actions';

import { FooterActions } from './components/FooterActions';
import { Header } from './components/Header';
import { StepLoading } from './components/StepLoading';
import { FixedFeeNewContractStepOne } from './components/StepOne';
import { StepSuccess } from './components/StepSuccess';
import { StepTwoSummary } from './components/StepTwo';
import { MIN_RATE } from './constants';
import { FixedFeeNewContractStepOneData, NewContractSteps } from './types';

const initialStepOneState: FixedFeeNewContractStepOneData = {
  searchTerm: '',
  selectedCustomer: undefined,
  sendingChannelsSelected: ['whatsapp'],
  excludeEquity: false,
  rate: MIN_RATE,
  adjustedNetWorth: undefined,
  canProceed: false,
  contractIdentifier: '',
};

export const FixedFeeNewContractModal: React.FC = () => {
  const { canGoBack, goBack } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route = useRoute<RouteProp<SignedInRootStackParamList, 'FixedFeeNewContractModal'>>();
  const sendTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const responsiveWidth = useBreakpointValue({
    base: 1,
    sm: 1,
    md: 0.42,
  });

  const { postCreateTerm } = useFixedFeeActions();

  const [stepOneData, setStepOneData] =
    React.useState<FixedFeeNewContractStepOneData>(initialStepOneState);
  const [currentStep, setCurrentStep] = React.useState<NewContractSteps>(1);
  const [loading, setLoading] = React.useState(false);
  const [termLoaded, setTermLoaded] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const handleClose = () => {
    if (sendTimeoutRef.current) {
      clearTimeout(sendTimeoutRef.current);
      sendTimeoutRef.current = null;
    }

    if (canGoBack()) {
      goBack();
    }
  };

  const handleBack = () => {
    if (currentStep === 2 && !success) {
      setCurrentStep(1);
      return;
    }

    handleClose();
  };

  const handleContinue = () => {
    if (currentStep === 1) {
      if (stepOneData.canProceed) {
        setCurrentStep(2);
      }
      return;
    }

    handleClose();
  };

  const handleSend = () => {
    setLoading(true);

    postCreateTerm(
      Number(stepOneData.selectedCustomer?.accountNumber),
      stepOneData.rate,
      stepOneData.sendingChannelsSelected,
      stepOneData.excludeEquity
    )
      .then(({ data }) => {
        setStepOneData((currentData) => ({
          ...currentData,
          contractIdentifier: data?.fixedFeeAgreement?.feeBasedContractHash || '--',
        }));
        setLoading(false);
        setSuccess(true);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    return () => {
      if (sendTimeoutRef.current) {
        clearTimeout(sendTimeoutRef.current);
      }
    };
  }, []);

  const handleSendingChannelsChange = React.useCallback((sendingChannelsSelected: string[]) => {
    setStepOneData((currentData) => {
      return {
        ...currentData,
        sendingChannelsSelected,
      };
    });
  }, []);

  const renderContent = () => {
    if (success) {
      return <StepSuccess stepOneData={stepOneData} onFollow={handleClose} />;
    }

    if (loading) {
      return <StepLoading isMobile={responsiveWidth === 1} />;
    }

    if (currentStep === 1) {
      return (
        <FixedFeeNewContractStepOne
          value={stepOneData}
          onChange={setStepOneData}
          initialReplacementSelection={route.params?.replaceTermContext}
        />
      );
    }

    if (currentStep === 2) {
      return (
        <StepTwoSummary
          stepOneData={stepOneData}
          shouldLoadTerm={currentStep === 2}
          onTermLoaded={() => setTermLoaded(true)}
          onSendingChannelsChange={handleSendingChannelsChange}
        />
      );
    }

    return null;
  };

  return (
    <SliderModal width={responsiveWidth} onDismiss={handleClose}>
      <VStack flex={1}>
        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          <VStack p="inset.sm" space="stack.xxxs">
            {!loading && (
              <Header currentStep={currentStep} hideSteps={success} onBack={handleBack} />
            )}

            {renderContent()}
          </VStack>
        </ScrollView>

        {!success && !loading && (
          <FooterActions
            currentStep={currentStep}
            isContinueDisabled={!stepOneData.canProceed}
            isSendDisabled={!stepOneData.sendingChannelsSelected.length || !termLoaded}
            onContinue={handleContinue}
            onCancel={handleClose}
            onSend={handleSend}
          />
        )}
      </VStack>
    </SliderModal>
  );
};
